import { JobCardRecommendation } from "./dispatcher/types";

// Add your feature-specific utilities here
export const wxUtils = {
  // NEW: Job card detail transformation utilities

  getConsumerFullName: (consumerInfo: any): string => {
    if (!consumerInfo) return "N/A";
    const { firstName, lastName } = consumerInfo;
    return [firstName, lastName].filter(Boolean).join(" ") || "N/A";
  },

  formatAddress: (addressData: any): string => {
    if (!addressData?.service) return "N/A";

    const { ADDRESS, UNIT, CITY, STATE, ZIPCODE } = addressData.service;
    const parts = [ADDRESS, UNIT, CITY, STATE, ZIPCODE].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  },

  // Transform job card detail data for UI components
  transformJobCardDetail: (data: any) => {
    let actualData = data;
    console.log("Raw Job Card Data:", data);

    // Check if data looks like an API response with a 'result' property
    if (data && typeof data === "object" && "result" in data && data.result) {
      actualData = data.result;
    }
    // If data already looks like the result object (has sop, consumer_info, etc.)
    else if (
      data &&
      typeof data === "object" &&
      ("sop" in data || "consumerInfo" in data)
    ) {
      actualData = data;
    }
    // If data is null/undefined/empty
    else if (!data) {
      return null;
    }

    // Add comprehensive validation
    if (!actualData || typeof actualData !== "object") {
      console.warn(
        "transformJobCardDetail: actualData is null, undefined, or not an object"
      );
      return null;
    }

    // Check if required nested objects exist
    if (!actualData.sop) {
      console.error(
        "transformJobCardDetail: actualData.sop is missing",
        actualData
      );
      return null;
    }

    // Make consumer_info optional and provide fallbacks
    let consumerInfo = actualData.consumerInfo;

    // If consumer_info is missing, try to construct it from other available data
    if (!consumerInfo) {
      console.warn(
        "consumer_info is missing, creating fallback from available data"
      );

      consumerInfo = {
        firstName: actualData.requestUsername?.split(" ")[0] || "",
        lastName:
          actualData.requestUsername?.split(" ").slice(1).join(" ") || "",
        consumerNo: actualData.requestAccountNo || "",
        contactNumber: actualData.mobileNumber || "",
        email: actualData.email,
        address: actualData.addressMap?.Address || "",
        addressData: {
          service: {
            ADDRESS: actualData.addressMap?.Address || "",
            UNIT: actualData.addressMap?.Unit || "",
            CITY: actualData.addressMap?.City || "",
            STATE: actualData.addressMap?.State || "",
            ZIPCODE: actualData.addressMap?.Zipcode || "",
          },
        },
      };
    }

    const sop = actualData.sop;
    const fieldForce = actualData.fieldForce; // This might be null
    let meterInfo = actualData.meterInfo;

    return {
      // Service Order Data
      serviceOrderData: {
        jobCardNo: actualData.jobCardNo,
        soName: sop?.name || "N/A",
        shortName: actualData.sop.shortName || "N/A",
        createdOn: actualData.createdDate,
        assignedOn: actualData.assignDate,
        expectedCompletion: actualData.expectedEndDate,
        utilityServices: Array.isArray(sop?.utilityServices)
          ? sop.utilityServices.join(", ")
          : "N/A",
        area: actualData.areaName || "N/A",
        subarea: actualData.subAreaName || "N/A",
        premise: actualData.premiseName || "N/A",
        taxRate: actualData.taxRate ? `${actualData.taxRate}%` : "N/A",
        description: sop?.description || "N/A",
        remark: actualData.remark || "No remarks",
        status: actualData.statusDisplay || "N/A",
        priority: actualData.priorityDisplay || "N/A",
        slaStatus: actualData.slaStatus || "NA",
        remainingTime: actualData.timeRemaining || "NA",
      },

      // Consumer Details - now using the fallback consumerInfo
      consumerDetails: {
        consumerName: wxUtils.getConsumerFullName(consumerInfo),
        accountNo:
          consumerInfo?.consumer_no || actualData.requestAccountNo || "N/A",
        phone: consumerInfo?.contactNumber,
        email: consumerInfo?.email || "N/A",
        address: consumerInfo?.address,
        area: consumerInfo?.territoryData?.service?.area,
        subarea: consumerInfo?.territoryData?.service?.subArea,
        premise: consumerInfo?.territoryData?.service?.premise,
        state: consumerInfo?.territoryData?.service?.state,
        consumerAddress: sop?.consumerAddress,
        consumerNo: consumerInfo?.consumerNo || "",
      },

      // Meter Details (handle null meter_info)
      meterDetails: {
        meterNo: meterInfo?.meterNumber || "N/A",
        currentReading: meterInfo?.currentReading || "",
        deviceNo: meterInfo?.deviceNo || "N/A",
        premise: meterInfo?.territoryData?.premise || "N/A",
        physicalStatus: meterInfo?.physicalStatusDisplay || "N/A",
        assignmentStatus: meterInfo?.statusDisplay || "N/A",
        readingStatus: meterInfo?.readingStatusDisplay || "N/A",
      },

      assetDetails: {
        assetId:
          actualData?.assetInfo?.assetId ||
          actualData.requestAccountNo ||
          "N/A",
        assetName:
          actualData?.assetInfo?.assetName ||
          actualData.requestUsername ||
          "N/A",
        location:
          actualData?.assetInfo?.location ||
          (actualData.areaName &&
          actualData.subAreaName &&
          actualData.premiseName
            ? `${actualData.areaName}, ${actualData.subAreaName}, ${actualData.premiseName}`
            : "N/A"),
        condition: actualData?.assetInfo?.condition || "N/A",
        rpn:
          actualData?.assetInfo?.rpn ||
          actualData?.assetInfo?.referenceNumber ||
          "N/A",
        status:
          actualData?.assetInfo?.statusDisplay ||
          actualData.statusDisplay ||
          "N/A",
        assetType: actualData?.assetInfo?.assetType || "N/A",
        serialNumber: actualData?.assetInfo?.serialNumber || "N/A",
        manufacturer: actualData?.assetInfo?.manufacturer || "N/A",
        installationDate: actualData?.assetInfo?.installationDate || "N/A",
        lastMaintenanceDate:
          actualData?.assetInfo?.lastMaintenanceDate || "N/A",
        nextMaintenanceDate:
          actualData?.assetInfo?.nextMaintenanceDate || "N/A",
      },

      // Technician Details (handle null field_force)
      technicianDetails: {
        technicianName:
          fieldForce?.name || actualData.assignedFieldForce || "Not Assigned",
        fid: fieldForce?.fieldForceNo || "N/A",
        phone: fieldForce?.contactNumber || "N/A",
        role: fieldForce?.role || "NA",
        email: fieldForce?.email || "N/A",
        experience: "N/A", // Not available in API
        specialization: sop?.sopAssociationTypeDisplay || "N/A",
      },

      // Job Overview
      jobOverview: {
        labourCost: 0, // Calculate based on staff_required * some rate
        materialCost: sop?.materialCostCount || 0,
        otherCost: 0, // Not available in API
        totalCost: actualData?.totalActualCost || 0,
        efforts: actualData?.totalInterval || "N/A",
        downtime: sop?.totalDowntime ? `${sop.totalDowntime} mins` : "N/A",
        requiredFieldforce: sop?.staffRequired
          ? `${sop.staffRequired} Technician${sop.staffRequired > 1 ? "s" : ""}`
          : "N/A",
        estimatedCompletion: formatDateTime(actualData.expectedEndDate),
      },

      // Tasks Data - safely handle array
      tasksData: Array.isArray(sop?.task)
        ? sop.task.map((task: any, index: number) => ({
            taskId: `T${String(index + 1).padStart(3, "0")}`,
            taskName: task?.name || "N/A",
            tatMins: parseInt(task?.time) || 0,
            downtimeMins: parseInt(task?.downtime) || 0,
            photoRequired: task?.isPhotoReq || false,
            status: index === 0 ? "In Progress" : "Pending",
            assignedTo:
              fieldForce?.name ||
              actualData.assignedFieldForce ||
              "Not Assigned",
            priority: index < (sop.task?.length || 0) / 2 ? "High" : "Medium",
          }))
        : [],

      // Materials Data - safely handle array
      materialsData: Array.isArray(sop?.material)
        ? sop.material.map((material: any, index: number) => ({
            materialId: `M${String(index + 1).padStart(3, "0")}`,
            name: material?.name || "N/A",
            size: material?.size || "N/A",
            quantity: parseInt(material?.quantityRequired) || 0,
            cost: parseInt(material?.cost) || 0,
            supplier: "N/A", // Not available in API
            status: index === 0 ? "Available" : "In Transit",
            deliveryDate: formatDate(actualData.createdDate),
          }))
        : [],

      // Readings Data - safely handle array
      readingsData: Array.isArray(sop?.readings)
        ? sop.readings.map((reading: any, index: number) => ({
            readingId: `R${String(index + 1).padStart(3, "0")}`,
            name: reading?.name || `Reading ${index + 1}`,
            unit: reading?.unit || "N/A",
            range:
              reading?.minimum && reading?.maximum
                ? `${reading.minimum}-${reading.maximum}`
                : "N/A",
            photoRequired: reading?.isPhotoReq || false,
            expectedValue: reading?.minimum || "N/A",
            actualValue: "N/A", // Not available in API
            status: "Pending",
            timestamp: "N/A",
          }))
        : [],

      // Help Resources Data - safely handle array
      helpResourcesData: Array.isArray(sop?.documents)
        ? sop.documents.map((doc: any, index: number) => ({
            file: doc?.file,
            link: doc?.link,
            resourceId: `H${String(index + 1).padStart(3, "0")}`,
            resourceName: doc?.description || `Document ${index + 1}`,
            resourceType: doc?.typeDisplay || "N/A",
            category: sop?.sopAssociationTypeDisplay || "N/A",
            duration: "15 mins",
            description: doc?.description || "NA",
            views: Math.floor(Math.random() * 1000) + 100,
            rating: 4.5 + Math.random() * 0.5,
            lastAccessed: formatDate(actualData.createdDate),
          }))
        : [],
    };
  },

  // Helper function to validate job card data structure
  validateJobCardData: (data: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Handle the API response format: { result: JobCardDetailResult }
    const actualData = data?.result || data;

    if (!actualData) {
      errors.push("Data is null or undefined");
      return { isValid: false, errors };
    }

    // Check required top-level properties
    if (!actualData.sop) errors.push("Missing sop data");
    if (!actualData.consumerInfo) errors.push("Missing consumer_info data");
    if (!actualData.meterInfo) errors.push("Missing meter_info data");
    if (!actualData.fieldForce) errors.push("Missing field_force data");

    // Check nested required properties
    if (actualData.sop && !actualData.sop.name) errors.push("Missing sop.name");
    if (actualData.consumerInfo && !actualData.consumerInfo.consumerNo)
      errors.push("Missing consumer_info.consumer_no");
    if (actualData.meterInfo && !actualData.meterInfo.meterNumber)
      errors.push("Missing meter_info.meter_number");
    if (actualData.fieldForce && !actualData.field_force.name)
      errors.push("Missing field_force.name");

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

export const getPriorityColor = (priority: string | null | undefined) => {
  if (!priority || typeof priority !== "string") {
    return "bg-gray-100 text-gray-800 border-gray-200";
  }

  switch (priority.toLowerCase()) {
    case "critical":
      return "bg-red-100 text-red-800 border-red-200";
    case "high":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "medium":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "low":
      return "bg-green-100 text-green-800 border-green-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const getStatusColor = (status: string | null | undefined) => {
  if (!status || typeof status !== "string") {
    return "bg-gray-100 text-gray-800 border-gray-200";
  }

  const statusColors: Record<string, string> = {
    created: "bg-blue-100 text-blue-800 border-blue-200",
    overdue: "bg-red-100 text-red-800 border-red-200",
    "in progress": "bg-yellow-100 text-yellow-800 border-yellow-200",
    "in-progress": "bg-orange-100 text-orange-800 border-orange-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    approved: "bg-green-100 text-green-800 border-green-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
    accepted: "bg-green-100 text-green-800 border-green-200",
    refused: "bg-red-100 text-red-800 border-red-200",
  };

  return (
    statusColors[status.toLowerCase()] ||
    "bg-gray-100 text-gray-800 border-gray-200"
  );
};

export const getAssociationBadgeClass = (
  association: string | null | undefined
) => {
  if (!association || typeof association !== "string") {
    return "bg-gray-100 text-gray-800 border-gray-200";
  }

  switch (association.toLowerCase()) {
    case "meter":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "consumer":
      return "bg-green-100 text-green-800 border-green-200";
    case "asset":
      return "bg-orange-100 text-orange-800 border-orange-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

export const formatDateTime = (dateString: string | null | undefined) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch (error) {
    return "Invalid Date";
  }
};
// export const extractAndValidateJobCardId = (jobCardNo: string): string => {
//   // Extract numeric part
//   const numericPart = jobCardNo.replace(/\D/g, "");

//   return numericPart;
// };
export const transformJobCardRecommendation = (
  recommendation: JobCardRecommendation
): any => {
  return {
    id: recommendation.jobCardId,
    type: recommendation.sopName,
    location: `${recommendation.areaName}, ${recommendation.subAreaName}`,
    distance: `${recommendation.jobCardDistance} km`,
    priority: recommendation.priority,
    estimatedTime: "NA", // Default, could be calculated based on SOP
    matchPercentage: recommendation.matchPercentage,
    locationScore: recommendation.locationScore,
    skillScore: recommendation.skillScore,
    availabilityScore: recommendation.availabilityScore,
    premiseName: recommendation.premiseName,
    areaName: recommendation.areaName,
    subAreaName: recommendation.subAreaName,
  };
};
